import type { GameState } from '../types/game-state.ts';
import { TURN_TIME_LIMIT_MS } from '../types/game-state.ts';
import type { Tile } from '../types/tile.ts';
import { applyMove } from './move.ts';

export type AutoPassOptions = {
  now?: number;
};

export type AutoPassResult = {
  state: GameState;
  /** 패스 사유 — 로그/UX 메시지에 활용. */
  reason: 'DISCARDED' | 'RETURNED_TO_POOL' | 'ADVANCED';
};

/**
 * 턴 시간 만료 시 강제 자동 패스 처리.
 *
 * 동작:
 * - holding(source=DECK)이 있으면 → DISCARD_TO_POOL 적용 (다음 플레이어로 진행).
 * - holding(source=POOL)이 있으면 → 풀로 그대로 되돌리고 차례 종료. 의도적 DISCARD는 불가하지만,
 *   시간 만료라는 강제 상황에선 풀로 반환하는 게 합리적 (RULES §4 위반 아님 — 가져온 상태를 무효화).
 * - holding 없으면 → 별도 액션 없이 currentPlayerIdx만 advance + 새 deadline 부여.
 *
 * IN_PROGRESS가 아니면 그대로 반환 (no-op).
 * 플레이어 1명 이하인 경우도 그대로 반환.
 */
export function forceAutoPass(state: GameState, options: AutoPassOptions = {}): AutoPassResult {
  if (state.status !== 'IN_PROGRESS') {
    return { state, reason: 'ADVANCED' };
  }
  if (state.players.length < 2) {
    return { state, reason: 'ADVANCED' };
  }

  const now = options.now ?? Date.now();

  if (state.holding) {
    if (state.holding.source === 'POOL') {
      const returnedTile: Tile = {
        id: state.holding.id,
        number: state.holding.number,
        face: 'UP',
        resolvedNumber: state.holding.resolvedNumber,
      };
      const nextCurrentIdx = (state.currentPlayerIdx + 1) % state.players.length;
      return {
        state: {
          ...state,
          center: {
            deck: state.center.deck,
            openPool: [...state.center.openPool, returnedTile],
          },
          holding: null,
          currentPlayerIdx: nextCurrentIdx,
          bonusActive: false,
          turnDeadline: now + TURN_TIME_LIMIT_MS,
        },
        reason: 'RETURNED_TO_POOL',
      };
    }
    const { state: nextState } = applyMove(state, { type: 'DISCARD_TO_POOL' }, { now });
    return { state: nextState, reason: 'DISCARDED' };
  }

  const nextCurrentIdx = (state.currentPlayerIdx + 1) % state.players.length;
  return {
    state: {
      ...state,
      currentPlayerIdx: nextCurrentIdx,
      bonusActive: false,
      turnDeadline: now + TURN_TIME_LIMIT_MS,
    },
    reason: 'ADVANCED',
  };
}
